<template>
  <div class="checkout-container">
    <stepper-progress :current-step="2"></stepper-progress>

    <v-layout row wrap>
      <v-flex xs12>
        <div class="checkout-layout">
          <div class="checkout-main-content">
            <v-card flat class="checkout-card elevation-2">
              <v-form @submit.prevent="submitBooking" ref="form" v-model="valid">
                <v-container fluid class="form-grid">

                  <v-flex xs12 class="section-header">
                    <v-icon color="primary">mdi-account-circle</v-icon>
                    <span class="section-title">Personal Information</span>
                  </v-flex>

                  <v-flex md6 xs12 class="pa-2">
                    <v-text-field v-model="booking.first_name" label="First Name" readonly outlined dense
                      class="modern-input" prepend-inner-icon="mdi-account"></v-text-field>
                  </v-flex>

                  <v-flex md6 xs12 class="pa-2">
                    <v-text-field v-model="booking.last_name" label="Last Name" readonly outlined dense
                      class="modern-input" prepend-inner-icon="mdi-account"></v-text-field>
                  </v-flex>

                  <v-flex md6 xs12 class="pa-2">
                    <v-text-field v-model="booking.email" label="Email" readonly outlined dense
                      class="modern-input" prepend-inner-icon="mdi-email"></v-text-field>
                  </v-flex>

                  <v-flex md6 xs12 class="pa-2">
                    <v-text-field v-model="booking.phone" label="Phone" readonly outlined dense
                      class="modern-input" prepend-inner-icon="mdi-phone"></v-text-field>
                  </v-flex>

                  <v-flex md6 xs12 class="pa-2">
                    <v-text-field v-model="booking.country" label="Country" readonly outlined dense
                      class="modern-input" prepend-inner-icon="mdi-earth"></v-text-field>
                  </v-flex>

                  <v-flex md6 xs12 class="pa-2">
                    <v-text-field v-model="booking.city" label="City" readonly outlined dense
                      class="modern-input" prepend-inner-icon="mdi-city"></v-text-field>
                  </v-flex>

                  <v-flex xs12 class="pa-2">
                    <v-text-field v-model="booking.promo_code" label="Promo Code" outlined dense
                      class="modern-input" prepend-inner-icon="mdi-ticket-percent"
                      hint="Enter your promo code if you have one" persistent-hint></v-text-field>
                  </v-flex>

                  <v-flex xs12 class="section-header">
                    <v-icon color="primary">mdi-map-marker</v-icon>
                    <span class="section-title">Entry Information</span>
                  </v-flex>

                  <v-flex md6 xs12 class="pa-2">
                    <v-autocomplete label="Mode of Entry" v-model="booking.entry_mode"
                      :items="allEntryModes" item-text="entry_mode_name"
                      item-value="entry_mode_public_id" @change="onEntryModeChange"
                      :loading="$loadingTrue" outlined dense class="modern-input"
                      prepend-inner-icon="mdi-vector-polyline"></v-autocomplete>
                  </v-flex>

                  <v-flex md6 xs12 class="pa-2">
                    <v-autocomplete label="Point of Entry" v-model="booking.entry_point"
                      :items="allEntryPoints" item-text="gate_name" item-value="gate_public_id"
                      :loading="$loadingTrue" outlined dense class="modern-input"
                      prepend-inner-icon="mdi-gate"></v-autocomplete>
                  </v-flex>

                  <v-flex xs12 class="pa-2">
                    <v-text-field v-model="booking.mode_registration" label="Mode Registration Number"
                      outlined dense class="modern-input" prepend-inner-icon="mdi-numeric"></v-text-field>
                  </v-flex>

                  <v-flex xs12 class="pa-2">
                    <v-textarea v-model="booking.additional_note" label="Additional Note (Optional)"
                      outlined dense class="modern-input" rows="2"
                      prepend-inner-icon="mdi-note-text"></v-textarea>
                  </v-flex>

                  <v-flex xs12 v-if="proofOfResidency.length > 0" class="residency-section pa-2">
                    <div class="section-header clickable" @click="residency_proof = true">
                      <v-icon color="primary">mdi-file-document</v-icon>
                      <span class="section-title">Click Here To Proof Residency</span>
                    </div>

                    <v-dialog v-model="residency_proof" persistent max-width="800px">
                      <v-card class="proof-dialog elevation-4">
                        <v-card-title class="dialog-title">
                          Residency Proof Details
                          <v-spacer></v-spacer>
                          <v-btn icon @click="residency_proof = false">
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </v-card-title>
                        <v-card-text>
                          <div class="proof-grid">
                            <div v-for="(residency, key) in proofOfResidency" :key="key" class="proof-item">
                              <v-text-field readonly label="Residency"
                                v-model="proofOfResidency[key].payment_person" outlined dense></v-text-field>
                              <v-text-field readonly label="Point Of Proof"
                                v-model="proofOfResidency[key].proof_point" outlined dense></v-text-field>
                              <v-text-field label="First Name"
                                v-model="proofOfResidency[key].first_name" outlined dense></v-text-field>
                              <v-text-field label="Last Name"
                                v-model="proofOfResidency[key].last_name" outlined dense></v-text-field>
                              <v-text-field label="Document ID"
                                v-model="proofOfResidency[key].document_id" outlined dense></v-text-field>
                            </div>
                          </div>
                        </v-card-text>
                        <v-card-actions class="dialog-actions">
                          <v-btn color="primary" depressed @click="addProof"
                            :loading="$loadingTrue" class="action-btn">
                            <v-icon left>mdi-check</v-icon> Add Proof
                          </v-btn>
                          <v-btn color="secondary" depressed @click="residency_proof = false"
                            class="action-btn">
                            <v-icon left>mdi-close</v-icon> Cancel
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </v-flex>

                  <v-flex xs12 class="terms-section pa-2">
                    <div class="terms-item">
                      <router-link target="_blank" :to="{ name: 'PTermsAndConditions' }"
                        class="terms-link">
                        <v-icon small>mdi-file-document</v-icon>
                        Ol Pejeta Conservancy Terms and Conditions
                      </router-link>
                      <v-checkbox v-model="booking.terms" type="checkbox"
                        label="I agree to the terms and conditions" color="primary"
                        hide-details class="terms-checkbox"></v-checkbox>
                    </div>

                    <div class="terms-item">
                      <a class="terms-link">
                        <v-icon small>mdi-email-newsletter</v-icon>
                        Receive Emails and Newsletters
                      </a>
                      <v-checkbox v-model="booking.newsletter" type="checkbox"
                        label="I agree to receive promotional emails" color="primary"
                        hide-details class="terms-checkbox"></v-checkbox>
                    </div>

                    <div class="terms-item">
                      <a class="terms-link" @click="openDisclaimer">
                        <v-icon small>mdi-alert</v-icon>
                        View Liability Disclaimer (Required)
                      </a>
                      <v-checkbox v-model="booking.disclaimer" type="checkbox"
                        label="I accept the liability disclaimer" color="primary"
                        hide-details class="terms-checkbox"
                        :disabled="!disclaimerViewed"></v-checkbox>
                    </div>

                    <v-dialog v-model="showDisclaimer" persistent max-width="800px">
                      <v-card class="proof-dialog elevation-4">
                        <v-card-title class="dialog-title">
                          Liability Disclaimer
                          <v-spacer></v-spacer>
                          <v-btn icon @click="showDisclaimer = false">
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </v-card-title>
                        <v-card-text class="disclaimer-text">
                          <p class="disclaimer-paragraph"><strong>Please read the following disclaimer carefully before proceeding.</strong></p>
                          <p class="disclaimer-paragraph">By entering the Ol Pejeta Conservancy ("the Conservancy") for any purpose, I acknowledge the inherent risks to my life and property.</p>
                          <p class="disclaimer-paragraph">I confirm that I have read and agree to abide by the Conservancy's "Code of Conduct" to ensure my safety to a reasonable extent.</p>
                          <p class="disclaimer-paragraph">I understand and accept that the Conservancy contains dangerous wild animals, including but not limited to lions, rhinos, elephants, buffaloes, snakes, and insects.</p>
                          <p class="disclaimer-paragraph">By entering the Conservancy, I agree to the following:</p>
                          <ol class="disclaimer-list">
                            <li>Waive and release the Conservancy, Ol Pejeta Conservancy Limited, and its employees, agents, affiliates, and officers from any liability.</li>
                            <li>Hold them harmless from any injury, claim, accident, loss, or damage, including personal injury (fatal or otherwise) and damage to personal property, resulting from my entry.</li>
                            <li>This waiver applies to myself and any minors accompanying me, and is binding on my assigns, heirs, and legal representatives.</li>
                          </ol>
                          <p class="disclaimer-paragraph">If any portion of this waiver is deemed unenforceable, that portion shall be struck out, but the remaining provisions will remain in full force and effect.</p>
                          <p class="disclaimer-paragraph">This waiver is governed by the laws of Kenya.</p>
                        </v-card-text>
                        <v-card-actions class="dialog-actions">
                          <v-btn color="primary" depressed @click="acceptDisclaimer" class="action-btn">
                            <v-icon left>mdi-check</v-icon> Agree
                          </v-btn>
                          <v-btn color="secondary" depressed @click="showDisclaimer = false"
                            class="action-btn">
                            <v-icon left>mdi-close</v-icon> Close
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </v-flex>

                  <v-flex xs12 class="pa-2">
                    <v-btn depressed color="primary" block type="submit"
                      :disabled="!canSubmit || submitting" :loading="loading"
                      class="submit-btn" large>
                      <v-icon left>mdi-check-circle</v-icon>
                      Complete Booking
                      <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-flex>
                </v-container>
              </v-form>
            </v-card>
          </div>

          <div class="checkout-sidebar">
            <div class="sidebar-header-wrapper">
              <h3 class="sidebar-cart-title">
                <v-icon small class="mr-1">mdi-cart</v-icon>
                Your Booking Cart
              </h3>
            </div>
            <side-bar ref="sidebar"></side-bar>
          </div>
        </div>
      </v-flex>
    </v-layout>

    <v-btn class="toggle-mobile-sidebar-btn" fab small @click="toggleMobileSidebar"
      color="primary" v-if="showMobileSidebarButton">
      <v-badge :content="mobileCartCount" :value="mobileCartCount > 0"
        color="red" overlap>
        <v-icon>mdi-cart-outline</v-icon>
      </v-badge>
    </v-btn>

    <transition name="fade">
      <div class="mobile-sidebar-overlay" v-if="mobileSidebarOpen"
        @click="closeMobileSidebar"></div>
    </transition>

    <transition name="slide">
      <div class="mobile-sidebar-wrapper" v-if="mobileSidebarOpen">
        <div class="mobile-sidebar-header">
          <h3 class="mobile-sidebar-title">
            <v-icon small class="mr-1">mdi-cart</v-icon>
            Your Booking Cart
          </h3>
          <v-btn icon small @click="closeMobileSidebar" class="close-mobile-sidebar-btn">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </div>
        <side-bar ref="mobileSidebar"></side-bar>
      </div>
    </transition>
  </div>
</template>

<script>
import SideBar from '../../Public/Repeated/SideBar.vue'
import StepperProgress from './StepperProgress.vue';

export default {
  name: "GuestCheckout",

  components: {
    SideBar,
    StepperProgress
  },

  data() {
    return {
      loading: false,
      submitting: false,
      valid: true,
      residency_proof: false,
      showDisclaimer: false,
      disclaimerViewed: false,

      mobileSidebarOpen: false,
      showMobileSidebarButton: false,
      mobileCartCount: 0,

      localCheckIn: '',
      localCheckOut: '',
      localNights: 0,

      cache: {
        guests: [],
        inventory: [],
        facilities: [],
        vehicles: [],
        conservationAll: [],
        proofOfResidency: [],
        externalFacilities: [],
      },

      booking: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        code: '',
        promo_code: '',
        entry_mode: '',
        entry_point: '',
        mode_registration: '',
        additional_note: '',
        newsletter: false,
        terms: false,
        disclaimer: false,
      },

      donation: {
        amount: '',
        currency_obj: null,
        currency_id: '',
        currency_name: '',
        cause_obj: null,
        donation_to: '',
        cause: '',
      },

      donationsList: [],
    };
  },

  computed: {
    canAddDonation() {
      return this.donation.amount && this.donation.amount > 0 &&
             this.donation.currency_obj && this.donation.cause_obj;
    },

    canSubmit() {
      return this.booking.terms && this.booking.disclaimer;
    },

    userFullName() {
      var first = localStorage.getItem('user_first_name') || '';
      var last = localStorage.getItem('user_last_name') || '';
      return (first + ' ' + last).trim() || 'Guest User';
    },

    donationsTotal() {
      var total = 0;
      for (var i = 0; i < this.donationsList.length; i++) {
        total += parseFloat(this.donationsList[i].amount) || 0;
      }
      return total;
    },

    globalBookingDates() {
      if (this.$store.state.GlobalBookingStore) {
        return this.$store.state.GlobalBookingStore.booking_dates;
      }
      return null;
    },

    hasGlobalBookingDates() {
      var dates = this.globalBookingDates;
      return dates && dates.check_in && dates.check_out;
    },

    displayCheckIn() {
      if (this.hasGlobalBookingDates) {
        return this.formatDate(this.globalBookingDates.check_in);
      }
      var userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      if (userData.check_in) return this.formatDate(userData.check_in);
      return this.localCheckIn ? this.formatDate(this.localCheckIn) : '';
    },

    displayCheckOut() {
      if (this.hasGlobalBookingDates) {
        return this.formatDate(this.globalBookingDates.check_out);
      }
      var userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      if (userData.check_out) return this.formatDate(userData.check_out);
      return this.localCheckOut ? this.formatDate(this.localCheckOut) : '';
    },

    displayNights() {
      if (this.hasGlobalBookingDates && this.globalBookingDates.days) {
        return this.globalBookingDates.days;
      }
      var userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      if (userData.days) return userData.days;
      return this.localNights || this.calculateNights(this.displayCheckIn, this.displayCheckOut);
    },

    allConservationFees() { return this.cache.conservationAll; },
    chosenFacilities() { return this.cache.facilities; },
    chosenInventory() { return this.cache.inventory; },
    allVehicles() { return this.cache.vehicles; },
    proofOfResidency() { return this.cache.proofOfResidency; },
    chosenExternalFacilities() { return this.cache.externalFacilities; },

    totalPrice() {
      return Number(localStorage.getItem('booking_total') || 0);
    },

    browserCurrency() {
      if (this.$store.state.CurrencyStore) {
        return this.$store.state.CurrencyStore.browser_currency || 'KES';
      }
      return 'KES';
    },

    allCurrencies() {
      if (this.$store.state.CurrencyStore) {
        return this.$store.state.CurrencyStore.all_currencies || [];
      }
      return [];
    },

    allGates() {
      if (this.$store.state.GateStore) {
        return this.$store.state.GateStore.all_gates || [];
      }
      return [];
    },

    allEntryPoints() {
      var allEntry = this.allGates || [];
      if (!this.booking.entry_mode) return [];
      return allEntry.filter(function (g) {
        return g.entry_mode_public_id === this.booking.entry_mode;
      }, this);
    },
  },

  created() {
    this.refreshCache();
    this.loadUserData();
    this.loadDates();
    this.loadBookingData();
    this.loadDonations();

    this.$store.dispatch("get_page", "t&c");
    this.$store.dispatch("get_all_causes");
    this.$store.dispatch("get_full_conservation_fees");
    this.$store.dispatch("get_all_gates");
    this.$store.dispatch("get_all_entry_modes");
    this.$store.dispatch("get_all_currencies");

    // Only show disclaimer if user hasn't already viewed it
    if (!this.disclaimerViewed) {
      this.openDisclaimer();
    }

    this.updateMobileCartCount();
    this.$root.$on('cart-updated', this.updateMobileCartCount);
  },

  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
    this.$root.$off('cart-updated', this.updateMobileCartCount);
  },

  methods: {
    // ---- Cache management ----
    refreshCache() {
      this.cache.conservationAll = this.safeParse('conservation_data', []);
      this.cache.inventory = this.safeParse('activity_data', []);
      this.cache.facilities = this.safeParse('facility_data', []);
      this.cache.vehicles = this.safeParse('vehicle_data', []);
      this.cache.proofOfResidency = this.safeParse('residency_proof_data', []);
      this.cache.externalFacilities = this.safeParse('external_facility_data', []);
      this.cache.guests = this.cache.conservationAll.filter(function (g) {
        return Number(g.payment_guests) > 0;
      });
    },

    safeParse(key, fallback) {
      try {
        var raw = localStorage.getItem(key);
        if (!raw || raw === 'null' || raw === '[]') return fallback;
        return JSON.parse(raw);
      } catch (e) {
        return fallback;
      }
    },

    formatDate(d) {
      if (!d) return '';
      if (this.$moment) return this.$moment(d).format('MMM DD, YYYY');
      return d;
    },

    loadUserData() {
      var userData = this.safeParse('user_data', {});
      this.booking.first_name = localStorage.getItem('user_first_name') || userData.first_name || '';
      this.booking.last_name = localStorage.getItem('user_last_name') || userData.last_name || '';
      this.booking.email = localStorage.getItem('user_email') || userData.email || '';
      this.booking.phone = localStorage.getItem('user_phone') || userData.phone || userData.phone_number || '';
      this.booking.country = localStorage.getItem('user_country') || userData.country || '';
      this.booking.city = localStorage.getItem('user_city') || userData.city || '';
      this.booking.code = localStorage.getItem('user_country_code') || userData.country_code || userData.code || '';
    },

    loadDates() {
      var checkIn = null, checkOut = null, days = null;

      if (this.hasGlobalBookingDates) {
        checkIn = this.globalBookingDates.check_in;
        checkOut = this.globalBookingDates.check_out;
        days = this.globalBookingDates.days || 1;
      }

      if (!checkIn || !checkOut) {
        var userData = this.safeParse('user_data', {});
        checkIn = userData.check_in;
        checkOut = userData.check_out;
        days = userData.days || 1;
      }

      if (checkIn && checkOut && !days) {
        days = this.calculateNights(checkIn, checkOut);
      }

      if (checkIn && checkOut) {
        this.localCheckIn = checkIn;
        this.localCheckOut = checkOut;
        this.localNights = days || 1;
      }
    },

    calculateNights(checkIn, checkOut) {
      if (!checkIn || !checkOut || !this.$moment) return 0;
      return this.$moment(checkOut).diff(this.$moment(checkIn), 'days');
    },

    loadBookingData() {
      // Commit cached values to Vuex (no re-parse)
      if (this.cache.inventory.length > 0 && this.$store.commit) {
        this.$store.commit('CHOSEN_ACTIVITIES', this.cache.inventory);
      }
      if (this.cache.facilities.length > 0 && this.$store.commit) {
        this.$store.commit('CHOSEN_FACILITIES', this.cache.facilities);
      }
      if (this.cache.guests.length > 0 && this.$store.commit) {
        this.$store.commit('CHOSEN_CONSERVATION_FEES', this.cache.guests);
      }
      if (this.cache.vehicles.length > 0 && this.$store.commit) {
        this.$store.commit('CHOSEN_VEHICLES', this.cache.vehicles);
      }
    },

    loadDonations() {
      var saved = localStorage.getItem('donation_data');
      if (saved && saved !== 'null' && saved !== '[]') {
        try { this.donationsList = JSON.parse(saved); } catch (e) {}
      }
    },

    validateDonationAmount() {
      if (this.donation.amount && this.donation.amount < 0) this.donation.amount = 0;
    },

    changeCurrencyDonation(currencyObj) {
      if (!currencyObj) return;
      this.donation.currency_obj = currencyObj;
      this.donation.currency_id = currencyObj.currency_public_id;
      this.donation.currency_name = currencyObj.currency_name;
    },

    changeCauseDonation(causeObj) {
      if (!causeObj) return;
      this.donation.cause_obj = causeObj;
      this.donation.donation_to = causeObj.public_id;
      this.donation.cause = causeObj.name;
    },

    addDonation() {
      if (!this.donation.amount || this.donation.amount <= 0) {
        if (this.$toast) this.$toast.error('Please enter a valid donation amount');
        return;
      }
      if (!this.donation.currency_obj) {
        if (this.$toast) this.$toast.error('Please select a currency');
        return;
      }
      if (!this.donation.cause_obj) {
        if (this.$toast) this.$toast.error('Please select a cause to donate to');
        return;
      }

      this.donationsList.push({
        amount: parseFloat(this.donation.amount),
        currency_id: this.donation.currency_id,
        currency_name: this.donation.currency_name,
        donation_to: this.donation.donation_to,
        cause: this.donation.cause,
        cause_name: this.donation.cause_obj.name,
      });
      localStorage.setItem('donation_data', JSON.stringify(this.donationsList));

      if (this.$store.commit) this.$store.commit('PUBLIC_DONATION', this.donationsList);
      if (this.$toast) this.$toast.success('Donation added successfully!');

      this.donation = {
        amount: '', currency_obj: null, currency_id: '', currency_name: '',
        cause_obj: null, donation_to: '', cause: '',
      };
    },

    removeDonation(index) {
      this.donationsList.splice(index, 1);
      localStorage.setItem('donation_data', JSON.stringify(this.donationsList));
      if (this.$store.commit) this.$store.commit('PUBLIC_DONATION', this.donationsList);
      if (this.$toast) this.$toast.info('Donation removed');
    },

    openDisclaimer() {
      this.showDisclaimer = true;
      this.disclaimerViewed = true;
    },

    acceptDisclaimer() {
      this.booking.disclaimer = true;
      this.showDisclaimer = false;
    },

    onEntryModeChange() {
      // Clear stale entry point when mode changes
      this.booking.entry_point = '';
    },

    findEntryPoints() {
      return this.allEntryPoints;
    },

    addProof() {
      this.residency_proof = false;
    },

    toggleMobileSidebar() {
      this.mobileSidebarOpen = !this.mobileSidebarOpen;
      document.body.style.overflow = this.mobileSidebarOpen ? 'hidden' : '';
    },

    closeMobileSidebar() {
      this.mobileSidebarOpen = false;
      document.body.style.overflow = '';
    },

    checkScreenSize() {
      this.showMobileSidebarButton = window.innerWidth < 960;
      if (!this.showMobileSidebarButton) this.mobileSidebarOpen = false;
    },

    updateMobileCartCount() {
      // Match desktop cart count logic (Set-based dedupe)
      var total = 0;
      var conservation = this.safeParse('conservation_data', []);
      var vehicles = this.safeParse('vehicle_data', []);
      var facilities = this.safeParse('facility_data', []);
      var activities = this.safeParse('activity_data', []);

      if (conservation.length > 0) total += 1;
      if (vehicles.length > 0) total += 1;

      var uniqueFacilities = new Set();
      facilities.forEach(function (f) {
        uniqueFacilities.add(f.id || f.public_id || f.facility_id || f.name);
      });
      total += uniqueFacilities.size;

      var uniqueActivities = new Set();
      activities.forEach(function (a) {
        uniqueActivities.add(
          (a.name || a.activity_name) + '_' + a.date + '_' + (a.time || a.start_time)
        );
      });
      total += uniqueActivities.size;

      this.mobileCartCount = total;
    },

    formatNumber(value) {
      return Math.round(value).toLocaleString('en');
    },

    getCurrentConservationFees() {
      var s = this.$store.state.ConservationFeeStore;
      if (s) {
        return s.conservation_fees || s.all_conservation_fees || s.full_conservation_fees || [];
      }
      if (this.$store.state.conservation_fees) return this.$store.state.conservation_fees;
      if (this.$store.state.all_conservation_fees) return this.$store.state.all_conservation_fees;
      return [];
    },

    // Run once on create — NOT during submit
    realignConservationSchedules() {
      var stored = this.safeParse('conservation_data', []);
      if (stored.length === 0) return;

      var fees = this.getCurrentConservationFees();
      if (fees.length === 0) return;

      var feeMap = {};
      for (var j = 0; j < fees.length; j++) {
        if (fees[j].payment_public_id) {
          feeMap[fees[j].payment_public_id] = fees[j];
        }
      }

      var changed = false;
      for (var i = 0; i < stored.length; i++) {
        var guest = stored[i];
        var fee = feeMap[guest.payment_public_id];
        if (fee && fee.payment_schedule && guest.payment_schedule !== fee.payment_schedule) {
          guest.payment_schedule = fee.payment_schedule;
          changed = true;
        }
      }

      if (changed) {
        try {
          localStorage.setItem('conservation_data', JSON.stringify(stored));
          this.cache.conservationAll = stored;
          this.cache.guests = stored.filter(function (g) { return Number(g.payment_guests) > 0; });
        } catch (e) {}
      }
    },

    submitBooking() {
      var self = this;

      if (this.submitting) return; // Prevent double-submit

      if (!this.$refs.form.validate()) {
        if (this.$toast) this.$toast.error('Please fill all required fields');
        return;
      }

      if (!this.booking.terms || !this.booking.disclaimer) {
        if (this.$toast) this.$toast.error('Please accept terms and disclaimer');
        return;
      }

      if (!this.booking.first_name || !this.booking.last_name || !this.booking.email) {
        if (this.$toast) this.$toast.error('Please provide your name and email address');
        return;
      }

      var currencyId = this.getCurrencyId();
      if (!currencyId || currencyId === 'KES' || currencyId.length !== 36) {
        if (this.$toast) this.$toast.error('Currency information is missing. Please refresh the page and try again.');
        return;
      }

      // Refresh cache once, before reading
      this.refreshCache();

      var guests = this.cache.guests;
      var inventory = this.cache.inventory;
      var facilities = this.cache.facilities;
      var vehicles = this.cache.vehicles;

      if (guests.length === 0 && inventory.length === 0 &&
          facilities.length === 0 && vehicles.length === 0) {
        if (this.$toast) this.$toast.error('No booking items found. Please add activities or guests.');
        return;
      }

      if (guests.length === 0) {
        if (this.$toast) this.$toast.error('Please add at least one guest before completing the booking.');
        return;
      }

      this.submitting = true;
      this.loading = true;
      this.$store.dispatch('loading_true');

      var request = {
        guests: guests,
        inventory: inventory,
        facilities: facilities,
        vehicles: vehicles,
        donation: this.donationsList,
        proof_of_residency: this.cache.proofOfResidency,
        external_facilities: this.cache.externalFacilities,

        check_in: this.localCheckIn,
        check_out: this.localCheckOut,
        days: this.localNights,

        first_name: this.booking.first_name,
        last_name: this.booking.last_name,
        email: this.booking.email,
        phone: this.booking.phone,
        country: this.booking.country,
        country_code: this.booking.code,
        code: this.booking.code,
        country_details: { name: this.booking.country, code: this.booking.code },
        city: this.booking.city,

        entry_mode: this.booking.entry_mode,
        entry_point: this.booking.entry_point,
        mode_registration: this.booking.mode_registration,
        additional_note: this.booking.additional_note,
        promo_code: this.booking.promo_code,
        newsletter: this.booking.newsletter,

        type: 'GB601X10',
        currency: currencyId,
        currency_id: currencyId,
        session_id: localStorage.getItem('session_token') || localStorage.getItem('lkjhgfdsa') || '',
        user_public_id: localStorage.getItem('user_public_id') || ''
      };

      this.$store.dispatch('post_booking_public', request)
        .then(function (response) { self.handleBookingSuccess(response); })
        .catch(function (error) { self.handleBookingError(error); })
        .finally(function () {
          self.loading = false;
          self.submitting = false;
          self.$store.dispatch('loading_false');
        });
    },

    getBookingItems(key) {
      // Kept for backward compat — delegates to cache
      if (key === 'conservation_data') return this.cache.guests;
      if (key === 'activity_data') return this.cache.inventory;
      if (key === 'facility_data') return this.cache.facilities;
      if (key === 'vehicle_data') return this.cache.vehicles;
      return [];
    },

    getCurrencyId() {
      if (this.$store.state.CurrencyStore && this.$store.state.CurrencyStore.browser_currency_id) {
        return this.$store.state.CurrencyStore.browser_currency_id;
      }
      return 'KES';
    },

    handleBookingSuccess(response) {
      var bookingId = null;

      if (response && response.data) {
        var data = response.data;
        bookingId = (data.data && data.data.booking_public_id) ||
                    data.booking_public_id ||
                    data.id;
      }

      if (!bookingId) {
        if (this.$toast) this.$toast.error('Booking response was missing an ID. Please contact support.');
        return;
      }

      if (this.$toast) this.$toast.success('Booking created successfully!');
      localStorage.setItem('current_booking_id', bookingId);

      if (this.donationsList.length > 0) {
        localStorage.setItem('donation_data', JSON.stringify(this.donationsList));
      }

      this.$router.push({
        name: 'GuestPaymentInformation',
        params: { booking_id: bookingId }
      });
    },

    handleBookingError(error) {
      var message = 'Booking failed. Please try again.';
      if (error && error.response && error.response.data) {
        var data = error.response.data;
        if (typeof data === 'string') message = data;
        else if (data.messages) message = Array.isArray(data.messages) ? data.messages.join('; ') : String(data.messages);
        else if (data.message) message = Array.isArray(data.message) ? data.message.join('; ') : data.message;
        else if (data.error) message = data.error;
      } else if (error && error.message) {
        message = error.message;
      }
      if (this.$toast) this.$toast.error(message);
    }
  }
};
</script>

<style scoped>
@font-face {
  font-family: 'Earthwerk';
  src: url('../PizzaDude.dk-Earthwerk.otf');
  font-weight: normal;
  font-style: normal;
}

.checkout-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
}

.checkout-layout {
  display: flex;
  gap: 24px;
}

.checkout-main-content {
  flex: 1;
  min-width: 0;
}

.checkout-sidebar {
  width: 340px;
  flex-shrink: 0;
}

.checkout-card {
  border-radius: 8px !important;
  padding: 20px;
}

.sidebar-header-wrapper {
  background: #74a533;
  border-radius: 12px 12px 0 0;
  padding: 16px 20px;
}

.sidebar-cart-title {
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-grid {
  display: flex;
  flex-wrap: wrap;
  margin: -8px;
}

.section-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  margin-bottom: 8px;
  border-bottom: 2px solid #eee;
}

.section-title {
  font-weight: 600;
  color: #333;
  font-size: 16px;
}

.modern-input {
  margin-bottom: 8px;
}

.donation-btn {
  border-radius: 8px !important;
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%);
  color: white !important;
}

.submit-btn {
  border-radius: 8px !important;
  margin-top: 16px;
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%);
  color: white !important;
}

.terms-section {
  width: 100%;
  background: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
}

.terms-item {
  margin-bottom: 16px;
}

.terms-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #74a533;
  text-decoration: none;
  margin-bottom: 8px;
  font-size: 14px;
  cursor: pointer;
}

.terms-link:hover {
  text-decoration: underline;
}

.terms-checkbox {
  margin-top: 0;
}

.donations-list-card {
  background: #fef9e6;
  border: 1px solid #ffe0b3;
  border-radius: 8px;
  margin-top: 8px;
}

.donations-list-title {
  font-size: 14px;
  font-weight: 600;
  color: #74a533;
  padding: 12px 16px;
}

.donations-total {
  text-align: right;
  font-size: 14px;
  border-top: 1px solid #ffe0b3;
  padding-top: 12px;
  margin-top: 8px;
}

.disclaimer-text {
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.disclaimer-paragraph {
  margin-bottom: 12px;
}

.disclaimer-list {
  margin-bottom: 12px;
  padding-left: 20px;
}

.disclaimer-list li {
  margin-bottom: 8px;
}

.proof-dialog {
  border-radius: 8px !important;
}

.dialog-title {
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%);
  color: white;
  padding: 16px;
}

.proof-grid {
  padding: 16px 0;
}

.proof-item {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 16px;
}

.dialog-actions {
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.action-btn {
  border-radius: 8px !important;
}

.toggle-mobile-sidebar-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 998;
  background-color: #74a533 !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  display: none;
}

.mobile-sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.mobile-sidebar-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  width: 85%;
  max-width: 320px;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.mobile-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #74a533;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.mobile-sidebar-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
}

.close-mobile-sidebar-btn {
  color: white !important;
  background: rgba(255,255,255,0.2) !important;
  border-radius: 50% !important;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter, .slide-leave-to {
  transform: translateX(100%);
}

@media (max-width: 960px) {
  .checkout-layout {
    flex-direction: column;
  }

  .checkout-sidebar {
    display: none;
  }

  .toggle-mobile-sidebar-btn {
    display: flex;
  }
}

@media (max-width: 600px) {
  .checkout-container {
    padding: 10px;
  }

  .checkout-card {
    padding: 10px;
  }

  .proof-item {
    grid-template-columns: 1fr;
  }

  .mobile-sidebar-wrapper {
    width: 100%;
  }
}
</style>